"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Image from "@/components/CwfImage";
import {
  CWF_PURCHASE_CELEBRATION_EVENT,
  type CelebrationAccent,
  type PurchaseCelebrationDetail,
} from "@/lib/purchaseCelebration";
import {
  playDooberFly,
  playDooberLand,
  playUiCelebrate,
} from "@/lib/audio/play";

const ACCENT_PALETTE: Record<
  CelebrationAccent,
  { ring: string; sparkle: string; label: string }
> = {
  gold: {
    ring: "rgba(251,191,36,0.92)",
    sparkle: "#fcd34d",
    label: "#fde68a",
  },
  gem: {
    ring: "rgba(168,85,247,0.92)",
    sparkle: "#c4b5fd",
    label: "#ddd6fe",
  },
  energy: {
    ring: "rgba(56,189,248,0.92)",
    sparkle: "#7dd3fc",
    label: "#bae6fd",
  },
  rose: {
    ring: "rgba(244,114,182,0.92)",
    sparkle: "#fda4af",
    label: "#fbcfe8",
  },
};

type Burst = {
  id: string;
  detail: PurchaseCelebrationDetail;
  /** Resolved end-point for the flying icon. */
  ex: number;
  ey: number;
  /** Per-burst stable particle params (so we don't re-randomize on rerender). */
  sparkles: Array<{
    angle: number;
    distance: number;
    delay: number;
    size: number;
    rot: number;
  }>;
};

const BURST_SPARKLE_COUNT = 14;
const BURST_DURATION_MS = 900;
const ICON_FLIGHT_MS = 1100;
const LABEL_DURATION_MS = 1300;
const RING_DURATION_MS = 700;
const TOTAL_BURST_LIFE = Math.max(
  BURST_DURATION_MS,
  ICON_FLIGHT_MS,
  LABEL_DURATION_MS,
);

function newBurstId(): string {
  return `pc-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function resolveTarget(detail: PurchaseCelebrationDetail): {
  ex: number;
  ey: number;
} {
  const fallback = {
    ex: detail.source.x,
    ey: Math.max(40, detail.source.y - 220),
  };
  if (typeof document === "undefined" || !detail.targetSelector) {
    return fallback;
  }
  const el = document.querySelector(detail.targetSelector);
  if (!el) return fallback;
  const r = (el as Element).getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return fallback;
  return { ex: r.left + r.width / 2, ey: r.top + r.height / 2 };
}

function makeSparkles(seed: string): Burst["sparkles"] {
  /** Deterministic-ish per-id offsets so each particle differs. */
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const out: Burst["sparkles"] = [];
  for (let i = 0; i < BURST_SPARKLE_COUNT; i++) {
    h = Math.imul(h ^ i * 2654435761, 16777619);
    const r1 = ((h >>> 0) % 10000) / 10000;
    h = Math.imul(h ^ i, 2246822519);
    const r2 = ((h >>> 0) % 10000) / 10000;
    h = Math.imul(h ^ (i + 7), 3266489917);
    const r3 = ((h >>> 0) % 10000) / 10000;
    const baseAngle = (i / BURST_SPARKLE_COUNT) * Math.PI * 2;
    out.push({
      angle: baseAngle + (r1 - 0.5) * 0.55,
      distance: 110 + r2 * 110,
      delay: r3 * 120,
      size: 8 + r1 * 10,
      rot: r2 * 540 - 180,
    });
  }
  return out;
}

export function PurchaseCelebrationLayer() {
  const [bursts, setBursts] = useState<Burst[]>([]);
  /** Mark which bursts have had their imperative animations bound. */
  const animatedRef = useRef(new Set<string>());

  useEffect(() => {
    function onEvent(ev: Event) {
      const detail = (ev as CustomEvent<PurchaseCelebrationDetail>).detail;
      if (!detail || !detail.iconSrc) return;
      if (
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        if (!detail.silent) {
          try {
            playUiCelebrate();
          } catch {
            /* noop */
          }
        }
        return;
      }
      const id = newBurstId();
      const { ex, ey } = resolveTarget(detail);
      const burst: Burst = {
        id,
        detail,
        ex,
        ey,
        sparkles: makeSparkles(id),
      };
      setBursts((prev) => [...prev, burst]);
      if (!detail.silent) {
        try {
          playUiCelebrate();
        } catch {
          /* noop */
        }
        // Land cue near the end of the icon flight.
        window.setTimeout(() => {
          try {
            playDooberLand();
          } catch {
            /* noop */
          }
        }, Math.max(0, ICON_FLIGHT_MS - 140));
      }
      window.setTimeout(() => {
        setBursts((prev) => prev.filter((b) => b.id !== id));
        animatedRef.current.delete(id);
      }, TOTAL_BURST_LIFE + 80);
    }
    window.addEventListener(CWF_PURCHASE_CELEBRATION_EVENT, onEvent);
    return () =>
      window.removeEventListener(CWF_PURCHASE_CELEBRATION_EVENT, onEvent);
  }, []);

  if (bursts.length === 0) return null;

  return (
    <>
      {bursts.map((b) => (
        <BurstView key={b.id} burst={b} animatedRef={animatedRef} />
      ))}
    </>
  );
}

function BurstView({
  burst,
  animatedRef,
}: {
  burst: Burst;
  animatedRef: React.MutableRefObject<Set<string>>;
}) {
  const iconRef = useRef<HTMLDivElement | null>(null);
  const accent = burst.detail.accent ?? "gold";
  const palette = ACCENT_PALETTE[accent];

  useLayoutEffect(() => {
    if (animatedRef.current.has(burst.id)) return;
    animatedRef.current.add(burst.id);
    const el = iconRef.current;
    if (!el) return;
    if (!burst.detail.silent) {
      try {
        playDooberFly();
      } catch {
        /* noop */
      }
    }
    const dx = burst.ex - burst.detail.source.x;
    const dy = burst.ey - burst.detail.source.y;
    const arcLift = Math.min(220, Math.max(80, Math.abs(dx) * 0.25 + 110));
    /** Quadratic Bézier control above the line for a satisfying arc. */
    const cpx = dx * 0.5;
    const cpy = dy * 0.35 - arcLift;

    /** 14 keyframe samples along the path with spring & rotate. */
    const steps = 14;
    const keyframes: Keyframe[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      const x = u * u * 0 + 2 * u * t * cpx + t * t * dx;
      const y = u * u * 0 + 2 * u * t * cpy + t * t * dy;

      let scale: number;
      if (t < 0.18) {
        const k = t / 0.18;
        scale = 0.4 + k * 1.05;
      } else if (t < 0.32) {
        const k = (t - 0.18) / 0.14;
        scale = 1.45 - k * 0.25;
      } else if (t < 0.85) {
        scale = 1.2;
      } else {
        const k = (t - 0.85) / 0.15;
        scale = 1.2 - k * 0.85;
      }
      const rot = Math.sin(t * Math.PI * 2.2) * 14 * (1 - t);
      const opacity = t < 0.92 ? 1 : 1 - (t - 0.92) / 0.08;

      keyframes.push({
        transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) scale(${scale.toFixed(3)}) rotate(${rot.toFixed(2)}deg)`,
        opacity,
        offset: t,
      });
    }
    const anim = el.animate(keyframes, {
      duration: ICON_FLIGHT_MS,
      easing: "cubic-bezier(0.22, 0.84, 0.32, 1)",
      fill: "forwards",
    });
    return () => {
      try {
        anim.cancel();
      } catch {
        /* noop */
      }
    };
  }, [burst, animatedRef]);

  return (
    <div className="pointer-events-none fixed inset-0 z-[230] overflow-visible" aria-hidden>
      {/* Expanding accent ring */}
      <span
        className="absolute h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          left: burst.detail.source.x,
          top: burst.detail.source.y,
          border: `3px solid ${palette.ring}`,
          animation: `cwf-pc-ring ${RING_DURATION_MS}ms cubic-bezier(0.18,0.72,0.32,1) forwards`,
        }}
      />

      {/* Sparkle particle burst */}
      {burst.sparkles.map((s, i) => {
        const tx = Math.cos(s.angle) * s.distance;
        const ty = Math.sin(s.angle) * s.distance;
        return (
          <span
            key={`${burst.id}-sp-${i}`}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{
              left: burst.detail.source.x,
              top: burst.detail.source.y,
              width: s.size,
              height: s.size,
              animation: `cwf-pc-sparkle ${BURST_DURATION_MS}ms cubic-bezier(0.18,0.72,0.32,1) ${s.delay}ms forwards`,
              ["--cwf-pc-tx" as string]: `${tx}px`,
              ["--cwf-pc-ty" as string]: `${ty}px`,
              ["--cwf-pc-rot" as string]: `${s.rot}deg`,
            }}
          >
            <SparkleSvg fill={palette.sparkle} />
          </span>
        );
      })}

      {/* Flying purchased-item doober */}
      <div
        className="absolute"
        style={{
          left: burst.detail.source.x,
          top: burst.detail.source.y,
          transform: "translate(-50%, -50%)",
        }}
      >
        <div
          ref={iconRef}
          className="relative flex h-20 w-20 items-center justify-center will-change-transform"
          style={{
            filter:
              "drop-shadow(0 6px 16px rgba(0,0,0,0.45)) drop-shadow(0 0 18px rgba(251,191,36,0.55))",
          }}
        >
          <Image
            src={burst.detail.iconSrc}
            alt=""
            width={160}
            height={160}
            className="pointer-events-none h-20 w-20 select-none object-contain"
            draggable={false}
          />
        </div>
      </div>

      {/* Rising label (above source) */}
      {burst.detail.label ? (
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border-2 px-3 py-1 font-display text-[0.78rem] font-black uppercase tracking-wider drop-shadow-[0_3px_0_rgba(0,0,0,0.55)] sm:text-[0.84rem]"
          style={{
            left: burst.detail.source.x,
            top: burst.detail.source.y,
            color: "#1f1208",
            background: `linear-gradient(180deg, #fff7d6, ${palette.label})`,
            borderColor: "rgba(120, 53, 15, 0.85)",
            animation: `cwf-pc-label ${LABEL_DURATION_MS}ms cubic-bezier(0.22,0.84,0.32,1) forwards`,
          }}
        >
          {burst.detail.label}
        </span>
      ) : null}

      {/* Keyframes (scoped via tagged style; cheap to inject per burst). */}
      <style>{`
        @keyframes cwf-pc-ring {
          0% { transform: translate(-50%, -50%) scale(0.45); opacity: 0.95; border-width: 3px; }
          70% { transform: translate(-50%, -50%) scale(2.4); opacity: 0.55; border-width: 2px; }
          100% { transform: translate(-50%, -50%) scale(3.6); opacity: 0; border-width: 1px; }
        }
        @keyframes cwf-pc-sparkle {
          0%   { transform: translate(-50%, -50%) translate(0, 0) scale(0.45) rotate(0deg); opacity: 0; }
          12%  { transform: translate(-50%, -50%) translate(calc(var(--cwf-pc-tx) * 0.18), calc(var(--cwf-pc-ty) * 0.18)) scale(1.05) rotate(calc(var(--cwf-pc-rot) * 0.18)); opacity: 1; }
          70%  { transform: translate(-50%, -50%) translate(calc(var(--cwf-pc-tx) * 0.95), calc(var(--cwf-pc-ty) * 0.95)) scale(1) rotate(calc(var(--cwf-pc-rot) * 0.95)); opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(var(--cwf-pc-tx), var(--cwf-pc-ty)) scale(0.35) rotate(var(--cwf-pc-rot)); opacity: 0; }
        }
        @keyframes cwf-pc-label {
          0%   { transform: translate(-50%, -50%) translateY(0) scale(0.6); opacity: 0; }
          14%  { transform: translate(-50%, -50%) translateY(-22px) scale(1.12); opacity: 1; }
          24%  { transform: translate(-50%, -50%) translateY(-30px) scale(1.0); opacity: 1; }
          80%  { transform: translate(-50%, -50%) translateY(-78px) scale(1.0); opacity: 1; }
          100% { transform: translate(-50%, -50%) translateY(-110px) scale(0.92); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function SparkleSvg({ fill }: { fill: string }) {
  return (
    <svg viewBox="0 0 32 32" className="h-full w-full" aria-hidden>
      <path
        d="M16 1 L18.5 12.5 L30 16 L18.5 19.5 L16 31 L13.5 19.5 L2 16 L13.5 12.5 Z"
        fill={fill}
        stroke="rgba(120, 53, 15, 0.9)"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
    </svg>
  );
}
